import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { getUserPreferences } from 'utils/helpers';
import { commentSaga } from './saga';
import { ExtendedComment } from '../types';
import {
  updateCommentById,
  updateReplyById,
  addReplyToComment,
  removeCommentById,
  removeReplyFromComment,
  updateFilterCommentCount,
  updateMarkAsResolvedCount
} from '../utils';
export interface CardData {
  issueId: string;
  momentType: string;
  issueName: string;
}
export type CommunicationMode = 'EMAIL' | 'SLACK';

// Initialize communication mode from user preferences
const getInitialCommunicationMode = (): CommunicationMode => {
  try {
    const preferences = getUserPreferences();
    return preferences.communicationModePreference || 'EMAIL';
  } catch {
    return 'EMAIL';
  }
};

export interface CommentState {
  cardData: CardData;
  currentCommentId: string | null;
  selectedFilter: {
    key: string;
    item: any;
    filterLabel?: string;
  } | null;
  filterOptions: any[];
  selectedSection: {
    key: string;
    item: any;
    sectionLabel?: string;
  } | null;
  sectionOptions: any[];
  isLoading: boolean;
  isLoadingFilterOptions: boolean;
  error: string | null;
  comments: ExtendedComment[];
  apiResponseStatus: {
    status: 'SUCCESS' | 'ERROR' | null;
    message: string | null;
  };
  badgeCount: number;
  communicationMode: CommunicationMode;
}

export const initialState: CommentState = {
  cardData: { issueId: '', momentType: '', issueName: '' },
  currentCommentId: null,
  selectedFilter: null,
  filterOptions: [],
  selectedSection: null,
  sectionOptions: [],
  isLoading: false,
  isLoadingFilterOptions: false,
  error: null,
  comments: [],
  apiResponseStatus: {
    status: null,
    message: null
  },
  badgeCount: 0,
  communicationMode: getInitialCommunicationMode()
};

const slice = createSlice({
  name: 'comment',
  initialState,
  reducers: {
    setSelectedCardData: (state, action: PayloadAction<CardData>) => {
      state.cardData = action.payload;
    },
    setCurrentCommentId: (state, action: PayloadAction<string | null>) => {
      state.currentCommentId = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setLoadingFilterOptions: (state, action: PayloadAction<boolean>) => {
      state.isLoadingFilterOptions = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setFilterOptions: (state, action: PayloadAction<any[]>) => {
      state.filterOptions = action.payload;
    },
    setSectionOptions: (state, action: PayloadAction<any[]>) => {
      state.sectionOptions = action.payload;
    },
    resetCommentState: state => {
      state.selectedFilter = initialState.selectedFilter;
      state.filterOptions = [];
      state.selectedSection = initialState.selectedSection;
      state.sectionOptions = [];
      state.isLoading = false;
      state.isLoadingFilterOptions = false;
      state.error = null;
    },
    resetSelectedFilter: state => {
      state.selectedFilter = initialState.selectedFilter;
    },
    // Saga trigger actions
    filterChanged: (
      state,
      action: PayloadAction<{ key: string; item: any; filterLabel?: string }>
    ) => {
      state.selectedFilter = action.payload;
    },
    sectionChanged: (
      state,
      action: PayloadAction<{ key: string; item: any; sectionLabel?: string }>
    ) => {
      state.selectedSection = action.payload;
    },
    fetchFilterOptions: (
      state,
      action: PayloadAction<{ issueId: string; momentType: string }>
    ) => {
      // Trigger saga to fetch filter options for the given issueId and momentType
      state.isLoadingFilterOptions = true;
      state.error = null;
    },
    fetchSectionOptions: (state, action: PayloadAction<string>) => {
      // Trigger saga to fetch section options for the given issueId
      state.error = null;
    },
    setComments: (state, action: PayloadAction<ExtendedComment[]>) => {
      state.comments = action.payload;
    },
    fetchComments: (state, action: PayloadAction<string>) => {
      // Saga trigger to fetch comments for the given issueId
    },
    submitComment: (state, action: PayloadAction<any>) => {
      // Add skeleton comment to state immediately
      state.comments = [action.payload, ...state.comments];
      state.error = null;
    },
    replySubmitComment: (state, action: PayloadAction<any>) => {
      // Add skeleton reply to state immediately
      const { parentCommentId, ...replyData } = action.payload;
      state.comments = addReplyToComment(state.comments, parentCommentId, {
        ...replyData,
        isSkeleton: true
      });
      state.error = null;
    },
    deleteComment: (
      state,
      action: PayloadAction<{
        commentId: string;
        userId: string;
        commentSection: string;
        commentSectionCategory: string;
      }>
    ) => {
      // Add skeleton state to the comment being deleted
      const { commentId } = action.payload;
      state.comments = updateCommentById(
        state.comments,
        commentId,
        comment => ({ ...comment, isSkeleton: true })
      );
      state.error = null;
    },
    setCommentDeleted: (state, action: PayloadAction<string>) => {
      // Remove the deleted comment from state
      state.comments = removeCommentById(state.comments, action.payload);
    },
    removeDeleteSkeleton: (state, action: PayloadAction<string>) => {
      // Remove skeleton state when delete API fails
      state.comments = updateCommentById(
        state.comments,
        action.payload,
        comment => ({ ...comment, isSkeleton: false })
      );
    },
    deleteReply: (
      state,
      action: PayloadAction<{
        commentId: string;
        userId: string;
        parentCommentId: string;
      }>
    ) => {
      // Add skeleton state to the reply being deleted
      const { commentId, parentCommentId } = action.payload;
      state.comments = updateReplyById(
        state.comments,
        parentCommentId,
        commentId,
        reply => ({ ...reply, isSkeleton: true })
      );
      state.error = null;
    },
    setReplyDeleted: (
      state,
      action: PayloadAction<{ replyId: string; parentCommentId: string }>
    ) => {
      // Remove the deleted reply from the specific parent comment
      const { replyId, parentCommentId } = action.payload;
      state.comments = removeReplyFromComment(
        state.comments,
        parentCommentId,
        replyId
      );
      state.isLoading = false;
    },
    removeReplyDeleteSkeleton: (
      state,
      action: PayloadAction<{ commentId: string; parentCommentId: string }>
    ) => {
      // Remove skeleton state from reply when delete API fails
      const { commentId, parentCommentId } = action.payload;
      state.comments = updateReplyById(
        state.comments,
        parentCommentId,
        commentId,
        reply => ({ ...reply, isSkeleton: false })
      );
    },
    setCommentAdded: (
      state,
      action: PayloadAction<{
        comment: ExtendedComment;
        skeletonId: string;
        readByMention?: boolean;
      }>
    ) => {
      // Update skeleton comment with real data from API (just update ID and remove skeleton state)
      const { comment: newComment, skeletonId } = action.payload;
      state.comments = updateCommentById(
        state.comments,
        skeletonId,
        comment => ({
          ...comment,
          id: newComment.id, // Update with real database ID
          isSkeleton: false,
          readByMention: false
        })
      );
    },
    removeSkeletonComment: (state, action: PayloadAction<string>) => {
      // Remove skeleton comment when API fails
      state.comments = removeCommentById(state.comments, action.payload);
      state.isLoading = false;
    },
    removeSkeletonReply: (
      state,
      action: PayloadAction<{ replyId: string; parentCommentId: string }>
    ) => {
      // Remove skeleton reply when API fails
      const { replyId, parentCommentId } = action.payload;
      state.comments = removeReplyFromComment(
        state.comments,
        parentCommentId,
        replyId
      );
    },
    setReplyAdded: (
      state,
      action: PayloadAction<{
        reply: ExtendedComment;
        parentCommentId: string;
        skeletonId: string;
      }>
    ) => {
      const { reply, parentCommentId, skeletonId } = action.payload;
      state.comments = updateReplyById(
        state.comments,
        parentCommentId,
        skeletonId,
        replyItem => ({
          ...replyItem,
          id: reply.id, // Update with real database ID
          isSkeleton: false
        })
      );
    },
    setApiResponseStatus: (
      state,
      action: PayloadAction<{
        status: 'SUCCESS' | 'ERROR' | null;
        message: string | null;
      }>
    ) => {
      state.apiResponseStatus = action.payload;
    },
    editComment: (
      state,
      action: PayloadAction<{
        commentId: string;
        text: string;
        commentMentions: any[];
        userId: string;
        editedAt: string;
        commentType: string;
        section: string;
        sectionCategory: string;
        communicationMode: string | null;
      }>
    ) => {
      // Add edit skeleton state to the comment being edited
      const { commentId } = action.payload;
      state.comments = updateCommentById(
        state.comments,
        commentId,
        comment => ({ ...comment, isEditSkeleton: true })
      );
      state.error = null;
    },
    setCommentEdited: (
      state,
      action: PayloadAction<{
        commentId: string;
        text: string;
        commentMentions: any[];
        editedAt: string;
        communicationMode?: string | null;
      }>
    ) => {
      // Update the edited comment in state
      state.comments = updateCommentById(
        state.comments,
        action.payload.commentId,
        comment => ({
          ...comment,
          text: action.payload.text,
          commentMentions: action.payload.commentMentions,
          editedAt: action.payload.editedAt,
          communicationMode:
            (action.payload.communicationMode as CommunicationMode) ??
            comment.communicationMode,
          isEditSkeleton: false
        })
      );
    },
    removeEditSkeleton: (state, action: PayloadAction<string>) => {
      // Remove edit skeleton state when API fails
      state.comments = updateCommentById(
        state.comments,
        action.payload,
        comment => ({ ...comment, isEditSkeleton: false })
      );
    },
    editReply: (
      state,
      action: PayloadAction<{
        commentId: string;
        text: string;
        commentMentions: any[];
        userId: string;
        editedAt: string;
        commentReplyId: string;
        commentType: string;
        section: string;
        sectionCategory: string;
        communicationMode: string | null;
      }>
    ) => {
      // Add edit skeleton state to the reply being edited
      const { commentId, commentReplyId } = action.payload;
      state.comments = updateReplyById(
        state.comments,
        commentId,
        commentReplyId,
        reply => ({ ...reply, isEditSkeleton: true })
      );
      state.error = null;
    },
    setReplyEdited: (
      state,
      action: PayloadAction<{
        commentId: string;
        commentReplyId: string;
        text: string;
        commentMentions: any[];
        editedAt: string;
        communicationMode?: string | null;
      }>
    ) => {
      // Update the edited reply in state
      state.comments = updateReplyById(
        state.comments,
        action.payload.commentId,
        action.payload.commentReplyId,
        reply => ({
          ...reply,
          text: action.payload.text,
          commentMentions: action.payload.commentMentions,
          editedAt: action.payload.editedAt,
          communicationMode:
            (action.payload.communicationMode as CommunicationMode) ??
            reply.communicationMode,
          isEditSkeleton: false
        })
      );
    },
    removeReplyEditSkeleton: (
      state,
      action: PayloadAction<{ commentId: string; replyId: string }>
    ) => {
      // Remove edit skeleton state from reply when API fails
      const { commentId, replyId } = action.payload;
      state.comments = updateReplyById(
        state.comments,
        commentId,
        replyId,
        reply => ({ ...reply, isEditSkeleton: false })
      );
    },
    markStatus: (
      state,
      action: PayloadAction<{
        commentId: string;
        status: 'OPEN' | 'RESOLVED' | 'PENDING';
        userId: string;
        timestamp: string;
      }>
    ) => {
      // Saga trigger to mark comment status
      state.error = null;
    },
    setCommentStatus: (
      state,
      action: PayloadAction<{
        commentId: string;
        status: 'OPEN' | 'RESOLVED' | 'PENDING';
      }>
    ) => {
      // Update the comment status in state
      state.comments = updateCommentById(
        state.comments,
        action.payload.commentId,
        comment => ({
          ...comment,
          commentStatus: action.payload.status
        })
      );
      state.isLoading = false;
    },
    markAsRead: (
      state,
      action: PayloadAction<{
        commentId: string;
        uid: string;
        markAsRead: boolean;
        timestamp: string;
      }>
    ) => {
      state.error = null;
    },
    setCommentMarkedAsRead: (
      state,
      action: PayloadAction<{
        commentId: string;
        readTimestamp: string;
      }>
    ) => {
      // Update the comment with read timestamp
      state.comments = updateCommentById(
        state.comments,
        action.payload.commentId,
        comment => ({
          ...comment,
          readByMentionTimestamp: action.payload.readTimestamp
        })
      );
    },
    updateFilterCommentCount: (
      state,
      action: PayloadAction<{
        section: string;
        sectionCategory: string;
        increment: number;
        filterOptions: any[];
      }>
    ) => {
      // Update comment count for specific filter option using utility function
      const { section, sectionCategory, increment, filterOptions } =
        action.payload;
      state.filterOptions = updateFilterCommentCount(
        filterOptions,
        section,
        sectionCategory,
        increment
      );
    },
    updateMarkAsResolvedCount: (
      state,
      action: PayloadAction<{
        filterOptions: any[];
        increment: number;
      }>
    ) => {
      // Update resolved count using dedicated utility function
      const { filterOptions, increment } = action.payload;
      state.filterOptions = updateMarkAsResolvedCount(filterOptions, increment);
    },
    setBadgeCount: (state, action: PayloadAction<number>) => {
      state.badgeCount = action.payload;
    },
    setCommunicationMode: (state, action: PayloadAction<CommunicationMode>) => {
      state.communicationMode = action.payload;
    }
  }
});

export const { actions: commentActions, reducer } = slice;

export const useCommentSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: commentSaga });
  return { actions: slice.actions };
};
